import { TablePaginationConfig } from 'antd'
import { AddressLink, SearchVestingParams, VestingData } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { Abi, formatEther } from 'viem'

import abiVestingHMC from 'abi/abi-vesting-hmc.json'
import { AppAdmin } from 'api/app-admin'
import { ContractStatusEnum } from 'constants/index'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { useVestingContext } from 'context/vesting-context-provider'
import { publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

interface IHookVestingData {
  includeDataContract?: boolean
}

export const useVestingData = ({ includeDataContract = false }: IHookVestingData) => {
  const { scVesting, type, startClaim, firstRelease, cliffDuration, totalPeriods, timePerPeriod } =
    useVestingContext()

  const [loading, setLoading] = useState(false)

  const [data, setData] = useState<VestingData[]>([])

  const [total, setTotal] = useState(0)

  const defaultSearchParams = {
    ownerAddress: '',
    page: DEFAULT_PAGE_INDEX,
    limit: DEFAULT_PAGE_SIZE,
    status: null,
    type: type,
  }

  const [searchParams, setSearchParams] =
    useState<Partial<SearchVestingParams>>(defaultSearchParams)

  function getLockedAmount({
    totalAmount,
    startTime = 0,
    firstReleasePercent = 0,
    cliffDuration = 0,
    totalPeriods = 0,
    timePerPeriod = 0,
    claimedAmount = 0,
  }: {
    totalAmount: any
    startTime?: number
    firstReleasePercent?: number
    cliffDuration?: number
    totalPeriods?: number
    timePerPeriod?: number
    claimedAmount?: number
  }) {
    const currentTime = Math.floor(Date.now() / 1000)
    const elapsed = currentTime - startTime

    if (elapsed < 0) {
      return totalAmount - claimedAmount
    }

    let unlocked = 0

    // ✅ First release được unlock ngay tại startTime
    unlocked += (totalAmount * firstReleasePercent) / 100

    const remaining = totalAmount * (1 - firstReleasePercent / 100)

    // ✅ Nếu đã qua thời gian cliff → tính số đợt đã bắt đầu (bao gồm cả đợt đang diễn ra)
    if (elapsed >= cliffDuration && totalPeriods > 0 && timePerPeriod > 0) {
      const timeAfterCliff = elapsed - cliffDuration
      const periodsStarted = Math.floor(timeAfterCliff / timePerPeriod) + 1 // ✅ đợt vừa bắt đầu cũng được tính
      const actualPeriods = Math.min(periodsStarted, totalPeriods)

      unlocked += (remaining * actualPeriods) / totalPeriods
    }

    const locked = Math.max(totalAmount - unlocked, 0)
    return locked
  }

  const getDataContract = useCallback(
    async (data: VestingData[]) => {
      try {
        const contractUserInfo = [] as any[]
        const contractUserClaimable = [] as any[]
        data.map((item: VestingData) => {
          contractUserInfo.push({
            address: scVesting as AddressLink,
            abi: abiVestingHMC as Abi,
            functionName: 'userInfo',
            args: [item.ownerAddress],
          })

          contractUserClaimable.push({
            address: scVesting as AddressLink,
            abi: abiVestingHMC as Abi,
            functionName: 'claimAble',
            args: [item.ownerAddress],
          })
        })

        const [rsDataUserInfo, rsUserClaimable] = await Promise.all([
          publicWallet.multicall({
            contracts: contractUserInfo,
          }),
          publicWallet.multicall({
            contracts: contractUserClaimable,
          }),
        ])

        return {
          rsDataUserInfo,
          rsUserClaimable,
        }
      } catch (error) {
        logError('error----------------------', error)
      }
    },
    [scVesting]
  )

  const getData = useCallback(
    async (params: SearchVestingParams) => {
      setLoading(true)
      try {
        if (!type) return
        const res = await AppAdmin.getListVesting(params)
        if (res?.data?.code === 200) {
          const data = res?.data?.data?.results || []
          setTotal(res?.data?.data?.totalResults)
          if (data?.length) {
            if (includeDataContract) {
              const dataContract = await getDataContract(data)
              const resultData = data.map((item: VestingData, index: number) => {
                const rsUserInfo = dataContract?.rsDataUserInfo?.[index]
                const rsClaimable = dataContract?.rsUserClaimable?.[index]
                const totalToken = item.amountContract
                let claimed = 0,
                  unlock = 0
                if (rsUserInfo?.status === ContractStatusEnum.SUCCESS && rsUserInfo.result) {
                  claimed = Number(formatEther((rsUserInfo.result as any[])?.[1]))
                }

                if (rsClaimable?.status === ContractStatusEnum.SUCCESS && rsClaimable.result) {
                  unlock = Number(formatEther(rsClaimable?.result as bigint))
                }

                const totalLocked = getLockedAmount({
                  totalAmount: totalToken,
                  startTime: startClaim,
                  firstReleasePercent: firstRelease,
                  cliffDuration,
                  totalPeriods,
                  timePerPeriod,
                  claimedAmount: claimed,
                })

                return {
                  ...item,
                  unlock,
                  claimed,
                  totalLocked,
                }
              })
              setData(resultData)
            } else {
              setData(data)
            }
          } else {
            setData([])
          }
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logError('Error fetching data: ', error)
      } finally {
        setLoading(false)
      }
    },
    [
      cliffDuration,
      firstRelease,
      getDataContract,
      includeDataContract,
      startClaim,
      timePerPeriod,
      totalPeriods,
      type,
    ]
  )

  useEffect(() => {
    getData(searchParams)
  }, [getData, searchParams])

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: page, pageSize: limit } = pagination
    onChangeSearchParams({
      page: page,
      limit: limit,
    })
  }

  const onChangeSearchParams = (payload?: Partial<SearchVestingParams>) => {
    setSearchParams((pre) => {
      return {
        ...pre,
        ...payload,
      }
    })
  }

  const onLoadData = () => {
    onChangeSearchParams(defaultSearchParams)
    getData(defaultSearchParams)
  }

  return {
    loading,
    data,
    total,
    searchParams,
    getData,
    handleTableChange,
    onLoadData,
    onChangeSearchParams,
  }
}
